import { useAppSelector } from '@/app/hooks'
import { navigation, type NavSection } from './navigation'
import { SidebarNavItem } from './sidebar-nav-item'
import { Logo } from './logo'
import { useCanAccess, useCanPlatform, useUserType } from '@/features/auth/access'
import { cn } from '@/lib/utils'

/**
 * A section is visible only if at least one of its children is accessible.
 * We compute visibility per-child via the same access logic the items use,
 * so there's a single source of truth.
 */
function SidebarSection({ section, collapsed }: { section: NavSection; collapsed: boolean }) {
  // We can't call hooks in a loop conditionally, so render children and let
  // each decide; then hide the heading if the rendered group is empty.
  // Simplest robust approach: compute access flags for all children up front.
  const userType = useUserType()
  const flags = section.children.map((leaf) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const access = useCanAccess({ permission: leaf.permission, module: leaf.module, moduleAny: leaf.moduleAny })
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const platform = useCanPlatform(leaf.permission)
    // DistroOps internal admin items: gated by platform.* permission
    if (leaf.platform) return platform
    // Tenant items: hide from platform users entirely (no company context,
    // these pages would 422 on tenant-scoped endpoints).
    if (userType === 'super_admin' || userType === 'platform_staff') return false
    return access
  })

  const anyVisible = flags.some(Boolean)
  if (!anyVisible) return null

  return (
    <div className="space-y-1">
      {!collapsed && (
        <p className="px-3 pb-1 pt-4 font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
          {section.label}
        </p>
      )}
      {collapsed && <div className="my-2 border-t border-border/50" />}
      {section.children.map((leaf) => (
        <SidebarNavItem key={leaf.to} leaf={leaf} collapsed={collapsed} />
      ))}
    </div>
  )
}

export function Sidebar() {
  const collapsed = useAppSelector((s) => s.ui.sidebarCollapsed)

  return (
    <aside
      className={cn(
        'flex h-screen flex-col border-r border-border bg-card transition-all duration-200',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      <div className={cn('flex h-16 items-center border-b border-border px-4', collapsed && 'justify-center px-2')}>
        <Logo collapsed={collapsed} />
      </div>

      <nav className="scrollbar-thin flex-1 space-y-1 overflow-y-auto px-3 py-2">
        {navigation.map((item, idx) => {
          if (item.type === 'leaf') {
            return <SidebarNavItem key={item.to} leaf={item} collapsed={collapsed} />
          }
          return <SidebarSection key={`${item.label}-${idx}`} section={item} collapsed={collapsed} />
        })}
      </nav>
    </aside>
  )
}
